import React from 'react';
import { SandboxReadiness } from '@/services/api/sandbox';

type DeveloperTabLink = 'keys' | 'webhooks';

const Requirement: React.FC<{
  met: boolean;
  label: string;
  hint: string;
  action: string;
  onAction?: () => void;
}> = ({ met, label, hint, action, onAction }) => (
  <li className="flex items-start gap-2.5">
    <i
      className={`mt-px text-base ${
        met
          ? 'ri-checkbox-circle-fill text-success-base'
          : 'ri-close-circle-fill text-error-base'
      }`}
      aria-hidden="true"
    />
    <div className="min-w-0 flex-1">
      <span className="text-sm font-medium text-text-strong-950">{label}</span>
      <span className="sr-only">{met ? ' — ready' : ' — missing'}</span>
      {!met && (
        <p className="mt-0.5 text-xs text-text-sub-600">
          {hint}{' '}
          {onAction && (
            <button
              type="button"
              onClick={onAction}
              className="font-medium text-primary hover:underline"
            >
              {action}
            </button>
          )}
        </p>
      )}
    </div>
  </li>
);

/**
 * "Before you simulate": an active Sandbox key and an active Sandbox webhook
 * endpoint (the API refuses otherwise), each with the tab that fixes it.
 */
const SandboxSetupStatus: React.FC<{
  readiness: SandboxReadiness | null;
  onNavigate?: (tab: DeveloperTabLink) => void;
}> = ({ readiness, onNavigate }) => (
  <section
    aria-label="Sandbox setup"
    className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6"
  >
    <h2 className="text-base font-semibold text-text-strong-950">
      Before you simulate
    </h2>
    {!readiness ? (
      <p className="mt-3 text-xs text-text-soft-400">
        Checking your sandbox setup…
      </p>
    ) : (
      <>
        <ul className="mt-3 space-y-2.5">
          <Requirement
            met={readiness.hasKey}
            label="An active Sandbox API key"
            hint="Your system calls the sandbox with a dash_test_ key."
            action="Create one →"
            onAction={onNavigate ? () => onNavigate('keys') : undefined}
          />
          <Requirement
            met={readiness.hasEndpoint}
            label="An active Sandbox webhook endpoint"
            hint="Each simulated step sends its webhook there."
            action="Add one →"
            onAction={onNavigate ? () => onNavigate('webhooks') : undefined}
          />
        </ul>
        {readiness.message && (
          <p
            role="alert"
            className="mt-3 rounded-lg bg-error-lighter px-3 py-2 text-xs text-error-dark"
          >
            {readiness.message}
          </p>
        )}
      </>
    )}
  </section>
);

export default SandboxSetupStatus;
